import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper,
  CircularProgress,
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  InputAdornment,
  Chip,
  Alert
} from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import SearchIcon from '@mui/icons-material/Search';
import PODetailsDialog from './PODetailsDialog';
import { inventoryService } from '../services/inventory.service';

const Inventory = () => {
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Sorting state
  const [orderBy, setOrderBy] = useState('expiration_date');
  const [order, setOrder] = useState('asc');

  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  // Column visibility toggle
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // PO Dialog state
  const [selectedPO, setSelectedPO] = useState(null);
  const [poDialogOpen, setPODialogOpen] = useState(false);

  // Column configuration
  const [columns, setColumns] = useState([
    { id: 'inventory_id', label: 'ID', visible: true, sortable: true, width: 80 },
    { id: 'sku', label: 'SKU', visible: true, sortable: true, width: 120 },
    { id: 'product_name', label: 'Product Name', visible: true, sortable: true, width: 200 },
    { id: 'warehouse_id', label: 'Warehouse ID', visible: true, sortable: true, width: 130 },
    { id: 'warehouse_name', label: 'Warehouse Name', visible: true, sortable: true, width: 150 },
    { id: 'quantity', label: 'Quantity', visible: true, sortable: true, width: 110, numeric: true },
    { id: 'area', label: 'Area', visible: true, sortable: true, width: 100 },
    { id: 'zone', label: 'Zone', visible: true, sortable: true, width: 100 },
    { id: 'bin_location', label: 'Bin Location', visible: false, sortable: true, width: 120 },
    { id: 'expiration_date', label: 'Expiration Date', visible: true, sortable: true, width: 140 },
    { id: 'lot_number', label: 'Lot Number', visible: true, sortable: true, width: 120 },
    { id: 'original_po_number', label: 'PO Number', visible: true, sortable: true, width: 130 },
    { id: 'received_shipment_id', label: 'Shipment ID', visible: true, sortable: true, width: 130 }
  ]);

  const handleColumnToggleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleColumnToggleClose = () => {
    setAnchorEl(null);
  };

  const toggleColumnVisibility = (colId) => {
    setColumns(prev => prev.map(col => 
      col.id === colId ? { ...col, visible: !col.visible } : col
    ));
  };

  // Separate useEffect for data fetching
  useEffect(() => {
    fetchInventoryData();
  }, []); // Run once on component mount

  const fetchInventoryData = async () => {
    try {
      console.log('Fetching inventory data...');
      setLoading(true);
      setError(null);
      
      // Using inventoryService instead of direct fetch
      const result = await inventoryService.getCurrentInventory();
      
      console.log('Received data:', result);
      
      // Update this part to handle the correct response structure
      if (result.status === 'success' && result.inventory_data && result.inventory_data.data) {
        console.log('Setting row data:', result.inventory_data.data);
        console.log('Row data length:', result.inventory_data.data.length);
        setRowData(result.inventory_data.data);  // Changed to access the data array directly
        setLoading(false);  // Make sure we set loading to false after data is set
      } else {
        console.error('Invalid response structure:', result);
        setError('Invalid response structure from API');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching inventory data:', error);
      setError('Failed to fetch inventory data');
      setLoading(false);  // Make sure we set loading to false even on error
    }
  };

  // Sorting functions
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const descendingComparator = (a, b, orderBy) => {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  };

  // Pagination functions
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filter and sort data
  const filteredAndSortedData = React.useMemo(() => {
    let filtered = rowData;
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(item => 
        item.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.warehouse_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.lot_number?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply sorting
    return filtered.sort(getComparator(order, orderBy));
  }, [rowData, searchTerm, order, orderBy]);

  // Get paginated data
  const paginatedData = React.useMemo(() => {
    return filteredAndSortedData.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [filteredAndSortedData, page, rowsPerPage]);

  // Custom cell renderers
  const renderExpirationCell = (item) => {
    const status = item.expiration_status;
    const days = item.days_until_expiration;

    let color = 'default';
    switch (status) {
      case 'expired':
        color = 'error';
        break;
      case 'expiring_soon':
        color = 'warning';
        break;
      case 'expiring_month':
        color = 'success';
        break;
      default:
        color = 'default';
    }

    return (
      <Box>
        <Chip 
          label={item.expiration_date} 
          color={color} 
          size="small" 
          variant="outlined"
        />
        {days !== null && (
          <Typography variant="caption" display="block" color="text.secondary">
            {days > 0 ? `+${days}` : days} days
          </Typography>
        )}
      </Box>
    );
  };

  const renderPOCell = (item) => {
    if (!item.original_po_number) return '-';
    
    return (
      <Typography
        variant="body2"
        sx={{ 
          color: 'primary.main', 
          cursor: 'pointer', 
          textDecoration: 'underline',
          '&:hover': { color: 'primary.dark' }
        }}
        onClick={() => {
          setSelectedPO(item.original_po_number);
          setPODialogOpen(true);
        }}
      >
        {item.original_po_number}
      </Typography>
    );
  };

  const renderQuantityCell = (item) => {
    return (
      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
        {item.quantity?.toLocaleString()}
      </Typography>
    );
  };

  const renderCell = (item, column) => {
    switch (column.id) {
      case 'expiration_date':
        return renderExpirationCell(item);
      case 'original_po_number':
        return renderPOCell(item);
      case 'quantity':
        return renderQuantityCell(item);
      case 'warehouse_name':
        return (
          <Typography
            variant="body2"
            sx={{ 
              color: 'primary.main', 
              cursor: 'pointer', 
              textDecoration: 'underline',
              '&:hover': { color: 'primary.dark' }
            }}
            onClick={() => navigate(`/inventory/warehouse/${item.warehouse_id}`)}
          >
            {item.warehouse_name}
          </Typography>
        );
      case 'warehouse_id':
        return (
          <Typography
            variant="body2"
            sx={{ 
              color: 'primary.main', 
              cursor: 'pointer', 
              textDecoration: 'underline',
              '&:hover': { color: 'primary.dark' }
            }}
            onClick={() => navigate(`/inventory/warehouse/${item.warehouse_id}`)}
          >
            {item.warehouse_id}
          </Typography>
        );
      default:
        return item[column.id] || '-';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: '100%', overflow: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Inventory</Typography>
        <IconButton onClick={handleColumnToggleClick}>
          <ViewColumnIcon />
        </IconButton>
      </Box>
      
      {/* Search Bar */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by SKU, Product Name, Warehouse, or Lot Number..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          size="small"
        />
      </Box>
      
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleColumnToggleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <List sx={{ width: 200 }}>
          {columns.map((col) => (
            <ListItem key={col.id} dense>
              <ListItemIcon>
                <Checkbox
                  checked={col.visible}
                  onChange={() => toggleColumnVisibility(col.id)}
                />
              </ListItemIcon>
              <ListItemText primary={col.label} />
            </ListItem>
          ))}
        </List>
      </Popover>
      
      {rowData.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <Typography variant="h6" color="textSecondary">No inventory data available</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{
                bgcolor: 'grey.50',
                '& .MuiTableCell-head': {
                  color: 'text.secondary',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  borderBottom: '2px solid',
                  borderColor: 'divider',
                  py: 1.5
                }
              }}>
                {columns.filter(col => col.visible).map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.numeric ? 'right' : 'left'}
                    sortDirection={orderBy === column.id ? order : false}
                    sx={{ width: column.width }}
                  >
                    {column.sortable ? (
                      <TableSortLabel
                        active={orderBy === column.id}
                        direction={orderBy === column.id ? order : 'asc'}
                        onClick={() => handleRequestSort(column.id)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((item) => (
                <TableRow 
                  key={item.inventory_id}
                  sx={{
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                    '& .MuiTableCell-body': {
                      py: 1.25
                    }
                  }}
                >
                  {columns.filter(col => col.visible).map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.numeric ? 'right' : 'left'}
                    >
                      {renderCell(item, column)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filteredAndSortedData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}
      
      {/* PO Details Dialog */}
      <PODetailsDialog
        open={poDialogOpen}
        onClose={() => setPODialogOpen(false)}
        poNumber={selectedPO}
      />
      
      {/* Debug info */}
      {process.env.NODE_ENV === 'development' && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100' }}>
          <Typography variant="body2">
            Debug: Loading: {loading.toString()}, Row count: {rowData.length}, Filtered: {filteredAndSortedData.length}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Inventory; 